// import { Icon } from '@components';
import {
  Box,
  //  IconButton
} from '@material-ui/core';

import {
  MODULE_MODAL_Y_OFFSET_CORREC_NUM,
  MODULE_MODAL_Y_TEMPLATE_EDITOR_OFFSET,
  MODULE_MODAL_Y_DEPLOYMENT_HEADER_OFFSET,
} from '../SampleTemplate.const';

import { addWidths, resizeModalIframe } from './ModuleModal.utils';
import { useWrappModalHtmlElems } from './ModuleModal.hooks';
import { ModuleModalProps } from './ModuleModal.props';
import { useStyles } from './ModuleModal.styles';
import { HIGHLIGHTED_ELEM_BORDER_WIDTH, IFRAME_BOTTOM_OFFSET, MODULE_CONTENT_DIV_ID } from './ModuleModal.const';
import { useContext, useEffect, useRef, useState, memo, useCallback } from 'react';
import { variables } from '@styles';
import { HTMLElementWithDocument } from '../SampleTemplate.types';
import { TreatmentBuilderContext } from '@routes/TemplateView/TemplateView.const';
import { useAppDispatch } from '@store';
import { modules } from '@modules';
import { ViewportSize, ViewportSizeContext, ViewportWidth } from '@layouts';
// import { delay } from '@utils/func/func';

/**
 ModuleModal component
 @returns {JSX.Element}
 */

export const ModuleModal = memo(
  ({
    modalTopPosition,
    moduleHtmlElem,
    isTemplateEditor = false,
    isEditorChanged,
    templateStyles,
    templateIframeId = '',
    setIsEditorChanged,
    setRightPanelElem,
    onChange,
    onClose,
    onRemove,
    onInputBlur,
  }: ModuleModalProps): JSX.Element => {
    const styles = useStyles();
    const dispatch = useAppDispatch();

    const moduleContentDiv = useRef<HTMLDivElement>(null);

    const { redefinedDoc } = useWrappModalHtmlElems(
      moduleHtmlElem,
      isEditorChanged,
      'module-modal',
      templateStyles,
      templateIframeId,
      setRightPanelElem,
      onChange,
      isTemplateEditor,
      onInputBlur,
    );

    const templateIframe = document.getElementById(templateIframeId) as HTMLElementWithDocument;
    const templateHeight = templateIframe?.getBoundingClientRect().height;
    const { setDVItems, setModuleDataVisualizationStation } = useContext(TreatmentBuilderContext);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const [wrapperTopPadding, setWrapperTopPadding] = useState<number | string>(0);

    const { viewportSize } = useContext(ViewportSizeContext);

    const { chosenElemId } = useContext(TreatmentBuilderContext);

    const handleResizeWindow = () => {
      if (iframeRef.current) {
        resizeModalIframe(iframeRef.current as HTMLIFrameElement, isTemplateEditor);
      }
    };

    // resize the iframe container on resize
    useEffect(() => {
      window.addEventListener('resize', handleResizeWindow);

      // Cleanup on unmount
      return () => {
        window.removeEventListener('resize', handleResizeWindow);
      };
    }, []);

    // update the width of the module modal
    useEffect(() => {
      if (iframeRef.current) {
        const moduleContent = document.getElementById(MODULE_CONTENT_DIV_ID) as HTMLDivElement;

        if (viewportSize === ViewportSize.Mobile) {
          // const moduleWidth = addWidths(
          //   [ViewportWidth.Mobile, HIGHLIGHTED_ELEM_BORDER_WIDTH, HIGHLIGHTED_ELEM_BORDER_WIDTH],
          //   'px',
          // );
          const moduleWidth = ViewportWidth.Mobile;
          moduleContent.style.minWidth = moduleWidth;
          moduleContent.style.width = moduleWidth;
        } else {
          // const moduleWidth = addWidths(
          //   [ViewportWidth.Desktop, HIGHLIGHTED_ELEM_BORDER_WIDTH, HIGHLIGHTED_ELEM_BORDER_WIDTH],
          //   'px',
          // );
          const moduleWidth = ViewportWidth.Desktop;
          moduleContent.style.minWidth = moduleWidth;
          moduleContent.style.width = moduleWidth;
        }
      }
    }, [chosenElemId, viewportSize]);

    // update the width of the parent of the iframe
    useEffect(() => {
      if (iframeRef.current) {
        const iframe = iframeRef.current as HTMLIFrameElement;

        const parent = iframe.parentNode as HTMLElement;

        if (viewportSize === ViewportSize.Mobile) {
          parent.style.width = ViewportWidth.Mobile;
        } else {
          parent.style.width = ViewportWidth.Desktop;
        }

        if (iframe.contentDocument?.body) {
          iframe.style.height = `${iframe.contentDocument.body.getBoundingClientRect().height + IFRAME_BOTTOM_OFFSET}px`;
        }
      }
    }, [viewportSize]);

    /***************************************************
     * Resize width of parent element based on height to account for scrollbar
     */
    const adjustParentWidthForScrollbar = useCallback(() => {
      if (iframeRef.current) {
        const iframe = iframeRef.current as HTMLIFrameElement;
        const parent = iframe.parentNode as HTMLElement;

        if (iframe.contentDocument?.body && parent) {
          const iframeHeight = iframe.contentDocument.body.getBoundingClientRect().height;
          const parentHeight = parent.getBoundingClientRect().height;

          const baseWidth = viewportSize === ViewportSize.Mobile ? ViewportWidth.Mobile : ViewportWidth.Desktop;

          // If iframe is taller than parent, add 6px for scrollbar
          if (iframeHeight > parentHeight) {
            parent.style.width = `calc(${baseWidth} + 6px)`;
          } else {
            parent.style.width = baseWidth;
          }
        }
      }
    }, [viewportSize]);

    // Call on iframe load
    useEffect(() => {
      const iframe = document.getElementById('module-modal') as HTMLIFrameElement;
      if (iframe) {
        iframe.onload = () => {
          adjustParentWidthForScrollbar();
        };
      }
    }, [viewportSize, redefinedDoc, adjustParentWidthForScrollbar]);

    // Call when viewportSize changes
    useEffect(() => {
      adjustParentWidthForScrollbar();
    }, [adjustParentWidthForScrollbar, viewportSize]);
    /*********************************************************
     * END: Resize width of parent element based on height to account for scrollbar
     */

    useEffect(() => {
      return () => {
        setModuleDataVisualizationStation({});
        setDVItems([]);
        setIsEditorChanged(false);
        dispatch(modules.actions.resetModuleInfo());
      };
    }, [setModuleDataVisualizationStation, setDVItems, dispatch, setIsEditorChanged]);

    useEffect(() => {
      const iframe = document.getElementById('module-modal') as HTMLIFrameElement;

      // NOTE: the following onload does not appear to fire
      iframe.onload = () => {
        const frameDoc = iframe.contentDocument;

        const frameBody = frameDoc?.body;
        const frameBodyRect = frameBody?.getBoundingClientRect();

        const moduleContentDivEl = document.getElementById(MODULE_CONTENT_DIV_ID) as HTMLDivElement;
        moduleContentDivEl.style.height = `${frameBodyRect?.height}px`;
      };
    }, []);

    // resize the iframe parent element width to make space for scrollbar if the
    // iframe content height exceeds the parent height, which would cause a scrollbar
    // to appear and potentially cover content
    useEffect(() => {
      const iframeHeight = iframeRef.current?.getBoundingClientRect()?.height;
      const parentElement = iframeRef.current?.parentElement;
      const parentHeight = parentElement?.getBoundingClientRect()?.height;

      if (parentHeight && iframeHeight && iframeHeight > parentHeight) {
        if (viewportSize === ViewportSize.Mobile) {
          parentElement.style.minWidth = `calc(${ViewportWidth.Mobile} + 6px)`;
          parentElement.style.width = `calc(${ViewportWidth.Mobile} + 6px)`;
        } else {
          parentElement.style.minWidth = `calc(${ViewportWidth.Desktop} + 6px)`;
          parentElement.style.width = `calc(${ViewportWidth.Desktop} + 6px)`;
        }
      }
    }, [iframeRef.current?.contentDocument?.body, viewportSize]);

    // Reposition the modal wrapper
    const repositionModalWrapper = useCallback(() => {
      if (isTemplateEditor) {
        setWrapperTopPadding(0);
      } else {
        setWrapperTopPadding(
          `calc(13.9rem + ${
            modalTopPosition - MODULE_MODAL_Y_OFFSET_CORREC_NUM + MODULE_MODAL_Y_DEPLOYMENT_HEADER_OFFSET
          }px)`,
        );
        // setWrapperTopPadding(0);
      }
    }, [isTemplateEditor, modalTopPosition]);

    // reposition modal wrapper on viewportSize change
    useEffect(() => {
      repositionModalWrapper();
    }, [modalTopPosition, repositionModalWrapper, viewportSize]);

    // finally wrap the redefinedDoc in a full HTML document
    const srcDoc = `<!DOCTYPE html>
<html>
  <head>
    <title>Iframe Document</title>
  </head>
  <body>
    ${redefinedDoc}
  </body>
</html>`;

    return (
      <Box
        style={{
          // paddingTop: isTemplateEditor
          //   ? `calc(22.7rem + ${
          //       modalTopPosition - MODULE_MODAL_Y_OFFSET_CORREC_NUM + MODULE_MODAL_Y_TEMPLATE_EDITOR_OFFSET
          //     }px)`
          //   : `calc(22.7rem + ${
          //       modalTopPosition - MODULE_MODAL_Y_OFFSET_CORREC_NUM + MODULE_MODAL_Y_DEPLOYMENT_HEADER_OFFSET
          //     }px)`,
          // Not sure where the 13.9rem or offsets are coming from, but this seems to position the
          // modal correctly in both the template editor and deployment view, so leaving as is for now.
          paddingTop: wrapperTopPadding,
          position: isTemplateEditor ? 'static' : 'absolute',
          height: templateHeight ? `${templateHeight + 50}px` : 'calc(150vh + 24.2rem)',
          backgroundColor: isTemplateEditor
            ? variables.colorV2.body.background.default
            : variables.colorV2.body.background.alpha50Percent,
        }}
        className={styles.moduleModalWrapper}
      >
        <div id={MODULE_CONTENT_DIV_ID} className={styles.moduleContent} ref={moduleContentDiv}>
          <iframe ref={iframeRef} id="module-modal" className={styles.iframe} srcDoc={srcDoc} title="module-modal" />
        </div>
      </Box>
    );
  },
);

ModuleModal.displayName = 'ModuleModal';
